import { cn } from '@/lib/utils'
import { interestGroupBadgesOf, type Badge } from '@/shared/types/badge/badge'
import { InterestGroupShieldIcon } from './assets/interest-group-shield-icon'

export type InterestGroupBadgeSize = 'sm' | 'md' | 'lg'

/**
 * Sizing tiers, matching the variation across cards and detail pages: `sm` is
 * the compact card chip, `md` the inline-with-title chip, `lg` the prominent
 * detail-page chip.
 */
const sizes: Record<InterestGroupBadgeSize, { icon: string; text: string; gap: string }> = {
  sm: { icon: 'h-3 w-3', text: 'text-[10px]', gap: 'gap-0.5' },
  md: { icon: 'h-3 w-3', text: 'text-xs', gap: 'gap-1' },
  lg: { icon: 'h-4 w-4', text: 'text-sm', gap: 'gap-1' },
}

interface InterestGroupBadgeProps {
  /** One `kind: interest_group` entry from a payload's `badges` list. */
  badge: Badge
  size?: InterestGroupBadgeSize
  className?: string
}

/**
 * The interest-group badge: one shield glyph plus the group's `title`.
 *
 * Pure presentation of the entry it is handed. The server filters `badges`
 * per viewer (ADR-0085) — an interest-group entry is present only when the
 * viewer is a member of that group — so this component reads no auth context
 * and holds no per-group knowledge: the `title` is whatever the group node
 * says it is.
 */
export function InterestGroupBadge({ badge, size = 'md', className }: InterestGroupBadgeProps) {
  const dims = sizes[size]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center font-medium leading-none text-foreground/70',
        dims.gap,
        dims.text,
        className
      )}
      data-testid="interest-group-badge"
    >
      <InterestGroupShieldIcon className={dims.icon} />
      <span>{badge.title}</span>
    </span>
  )
}

interface InterestGroupBadgesProps {
  /** A payload's `badges` list, quality markers and all — it is filtered here. */
  badges: readonly Badge[] | null | undefined
  size?: InterestGroupBadgeSize
  /** Applied to each rendered badge. */
  className?: string
}

/**
 * Renders one `InterestGroupBadge` per interest-group entry in `badges`, and
 * nothing at all when there is none — so a card can drop it inline without
 * first asking whether the list has any.
 */
export function InterestGroupBadges({ badges, size, className }: InterestGroupBadgesProps) {
  const groups = interestGroupBadgesOf(badges)
  if (groups.length === 0) return null
  return (
    <>
      {groups.map((badge) => (
        <InterestGroupBadge key={badge.id} badge={badge} size={size} className={className} />
      ))}
    </>
  )
}
